import { api } from '../../../core/utils/apiClient';
import { Company } from '../../dashboard_user/types';
import { adminCompaniesService } from '../../admin/companies/services/admin-companies.service';
import { CompanySource, CompanySourceLoadResult } from './types';

/**
 * Builds an admin company source bound to a specific company id.
 *
 * Authorization note: the backend enforces access on every PUT/GET via
 * `verify_role_access` / `verify_company_read_access` (admins bypass via
 * `is_admin`). This adapter MUST NOT be used from a non-admin page — wiring
 * is enforced at the route boundary (`AdminRoute`), not in this file.
 */
export const makeAdminCompanySource = (companyId: number | null): CompanySource => ({
  async load(): Promise<CompanySourceLoadResult> {
    if (!companyId) {
      return { company: null };
    }

    const companyData = await adminCompaniesService.getCompanyById(companyId);
    if (!companyData) {
      throw new Error('Company not found');
    }

    // Use a spread + cast so sector_id (not on the Company interface but read by
    // mapCompanyToForm via `(company as any)?.sector_id`) survives the mapping.
    const mappedCompany = {
      id: companyData.id,
      company_id: String(companyData.id),
      name: companyData.name,
      website: companyData.website || '',
      industry: companyData.industry || '',
      location: companyData.location || '',
      domain: companyData.domain || '',
      founding_year: companyData.founding_year || 0,
      employees: companyData.employees || '',
      description: companyData.description || '',
      profile_completeness_score: companyData.profile_completeness_score || 0,
      enrichment_score: companyData.enrichment_score || 0,
      last_enrichment_at: '',
      created_at: companyData.created_at,
      updated_at: companyData.updated_at ?? companyData.created_at,
      created_by: companyData.created_by || '',
      is_active: companyData.is_active !== undefined ? companyData.is_active : true,
      sector_id: companyData.sector_id ?? null,
    } as Company;

    return { company: mappedCompany };
  },

  async save(form, current): Promise<Company> {
    if (!current?.id) {
      throw new Error('Admin save requires an existing company id');
    }

    const payload = {
      name: form.name,
      domain: form.domain,
      description: form.description,
      website: form.website,
      founding_year: form.founding_year ? parseInt(form.founding_year) : null,
      employees: form.employees,
      industry: form.industry,
      location: form.location,
      sector_id: form.sector_id ?? undefined,
      linkedin_url: '',
      auto_enrich: true,
    };

    const response = await api.put(`/api/v1/companies/${current.id}`, payload);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to save company');
    }

    const savedCompany = await response.json();
    return { ...current, ...savedCompany };
  },
});
